import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildPageMeta } from "@/lib/seoIndex";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();

  useSEO({ robots: decideRobots({ pageType: "auth" }), ...buildPageMeta("auth") });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav(params.get("redirect") || "/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="login-page">
      <p className="eyebrow">{t("login.eyebrow")}</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">{t("login.title")}</h1>
      <p className="mt-2 text-sm text-[#5F6B61]">{t("login.subtitle")}</p>

      <form onSubmit={submit} className="mt-8 space-y-4 card-clay p-7" data-testid="login-form">
        <Input label={t("login.email")} type="email" value={email} onChange={setEmail} testId="login-email"/>
        <Input label={t("login.password")} type="password" value={password} onChange={setPassword} testId="login-password"/>
        {error && <div className="text-sm text-[#3F6147] bg-[#E9F0E0] border border-[#5B8C66]/20 rounded-xl px-4 py-3" data-testid="login-error">{error}</div>}
        <button type="submit" disabled={loading} className="btn-pill w-full px-5 py-3 bg-[#5B8C66] text-white font-bold hover:bg-[#3F6147] transition disabled:opacity-60" data-testid="login-submit">
          {loading ? t("login.submitting") : t("login.submit")}
        </button>
      </form>

      <p className="mt-6 text-sm text-center text-[#5F6B61]">
        {t("login.no_account")} <Link to="/register" className="text-[#5B8C66] font-bold">{t("login.create_account")}</Link>
      </p>
      <p className="mt-2 text-sm text-center">
        <Link to="/forgot-password" className="text-[#5F6B61] hover:text-[#5B8C66] font-medium" data-testid="login-forgot-link">{t("login.forgot")}</Link>
      </p>
    </div>
  );
}

function Input({ label, type, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[#3F6147] mb-2 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full px-4 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white"
        data-testid={testId}
      />
    </div>
  );
}
